import React, { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import ErrorBanner from "../ui/ErrorBanner";
import SkeletonList from "../ui/SkeletonList";

import { IoMdArrowBack } from "react-icons/io";
import { IoClose } from "react-icons/io5";

import { useLoading } from "../../context/LoadingContext";
import { formatCurrency } from "../../utils/global";
import FloatingMenu from "../FloatingMenu";
import { useNavigate } from "react-router-dom";
import { fetchBalance, fetchUserTransactionHistory } from "../../utils/api";
import config from '../../config';
import { assetPath } from "../../utils/assets";

const WalletPage = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoading();
    const navigate = useNavigate();

    const [errorMessage, setErrorMessage] = useState("");

    const [balanceLoading, setBalanceLoading] = useState(true);
    const [balance, setBalance] = useState(0);

    const [transactionHistoryLoading, setTransactionHistoryLoading] = useState(true);
    const [transcationHistory, setTranscationHistory] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setBalanceLoading(true);
            setTransactionHistoryLoading(true);
            setErrorMessage("");

            try {
                const [balanceResponse, historyResponse] = await Promise.all([
                    fetchBalance(),
                    fetchUserTransactionHistory()
                ]);

                if (balanceResponse.success) {
                    setBalance(balanceResponse.data);
                } else {
                    setBalance(0);
                }

                setTranscationHistory(
                    historyResponse.success && Array.isArray(historyResponse.data) && historyResponse.data.length > 0
                    ? historyResponse.data
                    : []
                );
            } catch (error) {
                setErrorMessage(t("pleaseTryLater"));
            } finally {
                setLoading(false);
                setBalanceLoading(false);
                setTransactionHistoryLoading(false);
            }
        };

        loadData();
    }, [setLoading, t]);

    useEffect(() => {
        if (errorMessage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [errorMessage]);

    return (
        <section className="page-shell">
            <div className="page-header">
                <div
                    className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                    onClick={() => navigate("/")}
                >
                    <IoMdArrowBack className="size-sm" />
                    <span className="text-sm">
                        {t("backToLobby")}
                    </span>
                </div>
                <button className="cursor-pointer" onClick={() => navigate("/")}>
                    <IoClose className="size-lg text-color-blue" />
                </button>
            </div>

            <ErrorBanner message={errorMessage} />

            {balanceLoading ? (
                <SkeletonList rows={1} height={96} className="p-4 rounded-lg" />
            ) : (
                <div className="p-4">
                    <div className="flex-grow dropdown-gradient-border-second mb-6 text-white h-24 flex flex-col items-center px-4 py-2 rounded-2xl">
                        <div className="flex justify-start w-full pt-2 pl-4">
                            <span className="text-sm font-bold">
                                {t("walletBalance")}
                            </span>
                        </div>
                        <div className="flex-grow flex-center">
                            <span className="text-4xl font-bold">
                                {`${config.CURRENCY_ISO_CODE} ${formatCurrency(balance, config.CURRENCY_ISO_CODE)}`}
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-around border-b border-border-line mb-6">
                        <button
                            className="pb-2 px-4 border-b-2 border-color-green font-bold"
                        >
                            <span className="text-white">
                                {t("history")}
                            </span>
                        </button>
                    </div>

                    <div>
                        {transactionHistoryLoading ? (
                            <SkeletonList
                                rows={transcationHistory.length || 1}
                                className="rounded-lg"
                            />
                        ) : (
                            <div className="rounded-lg bg-option-blue mb-6">
                                {transcationHistory.length > 0 ? (
                                    transcationHistory.map((transaction) => {
                                        const iconSrc = transaction.transaction_type === "IN" ? assetPath("/icons/in.png") : assetPath("/icons/out.png");

                                        const getMethod = (channel_id) => {
                                            switch (channel_id) {
                                                case 21: return t("Online Transfer");
                                                case 24: return t("Payment Gateway");
                                                case 25: return t("QRpay");
                                                case 26: return t("Crypto");
                                                case 38: return t("Qris");
                                                default: return "";
                                            }
                                        };

                                        const getStatusLabel = (status) => {
                                            switch (status) {
                                                case 1: return t("Pending");
                                                case 0: return t("Success");
                                                case 2: return t("Rejected");
                                                default: return "";
                                            }
                                        };

                                        return (
                                            <div
                                                key={transaction.id}
                                                className="container-equal-lg justify-start gap-2 cursor-pointer text-white 
                                                border-b border-option-selected-blue first:rounded-t-lg last:rounded-b-lg last:border-b-0"
                                            >
                                                <div className={`p-3 rounded-full ${transaction.transaction_type === "IN" ? "bg-color-blue" : "bg-secondary"}`}>
                                                    <img src={iconSrc} className="w-3 h-3" alt="" />
                                                </div>
                                                <div className="flex-between flex-grow">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm">
                                                            {transaction.fund_method === "Reward" ? t("referralCashback") : t(transaction.fund_method)} : {getMethod(transaction.channel_id)}
                                                        </span>
                                                        <span className="text-xs text-color-blue">
                                                            {new Date(transaction.created_at).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col items-end">
                                                        <span className="text-sm">
                                                            {transaction.transaction_type === "IN" ? "+" : "-"} {transaction.amount}
                                                        </span>
                                                        <span className="text-xs text-color-blue">
                                                            {getStatusLabel(transaction.pending_status)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="empty-state">
                                        {t("NoTransactionHistoryAvailable")}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FloatingMenu />
        </section>
    );
};

export default WalletPage;